import debounce from 'lodash/debounce'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  authenticate,
  requestPasswordReset,
  verifyResetToken,
  resetPassword,
} from '@/lib/auth'
import { componentStyles } from '@/lib/theme'
import { useThemeMode } from '@/contexts/ThemeContext'
import ConfirmationScreen from './auth/ConfirmationScreen'
import ForgotPasswordForm from './auth/ForgotPasswordForm'
import LoginSignupForm from './auth/LoginSignupForm'
import ResetPasswordForm from './auth/ResetPasswordForm'
import TOSModal from './auth/TOSModal'

type AuthMode =
  | 'login'
  | 'signup'
  | 'forgot-password'
  | 'reset-password'
  | 'confirm'

interface AuthWindowProps {
  onAuthSuccess: () => void
  extraData?: {
    mode?: AuthMode
  }
  showFooter?: boolean
}

function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

function validatePassword(password: string): boolean {
  return password.length >= 8
}

export default function AuthWindow({
  onAuthSuccess,
  extraData,
  showFooter = false,
}: AuthWindowProps) {
  const _navigate = useNavigate()
  const { colors } = useThemeMode()
  const [authMode, setAuthMode] = useState<AuthMode>(extraData?.mode || 'login')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false)
  const [sessionEstablished, setSessionEstablished] = useState(false)
  const [tosAccepted, setTosAccepted] = useState(false)
  const [showTOSModal, setShowTOSModal] = useState(false)

  const checkEmail = useMemo(
    () =>
      debounce(async (email: string) => {
        if (!validateEmail(email)) {
          setError('Please enter a valid email address')
        } else {
          setError(null)
        }
      }, 250),
    []
  )

  const checkPassword = useMemo(
    () =>
      debounce(async (password: string) => {
        if (!validatePassword(password)) {
          setError('Password must be at least 8 characters long')
        } else {
          setError(null)
        }
      }, 250),
    []
  )

  const checkPasswordMatch = useMemo(
    () =>
      debounce(async (password: string, confirmPassword: string) => {
        if (password !== confirmPassword) {
          setError('Passwords do not match')
        } else if (!validatePassword(password)) {
          setError('Password must be at least 8 characters long')
        } else {
          setError(null)
        }
      }, 250),
    []
  )

  useEffect(() => {
    const establishSession = async () => {
      // Check for recovery query parameters (PKCE flow)
      const searchParams = new URLSearchParams(window.location.search)
      const typeParam = searchParams.get('type')
      const tokenHash = searchParams.get('token_hash')

      const hasRecoveryToken = tokenHash && typeParam === 'recovery'

      if (hasRecoveryToken && authMode !== 'reset-password') {
        setAuthMode('reset-password')
        return // Let the effect run again with the new mode
      }

      // If we're in reset-password mode and have a token, verify it with backend
      if (authMode === 'reset-password' && hasRecoveryToken) {
        setIsLoading(true)
        setError(null)

        // Call backend to verify the token and establish session
        const result = await verifyResetToken(tokenHash, typeParam)
        setIsLoading(false)

        if (result.error) {
          console.error('Token verification error:', result.error)
          setError(result.error)
          const url = new URL(window.location.href)
          if (
            url.searchParams.has('token_hash') ||
            url.searchParams.has('type')
          ) {
            url.searchParams.delete('token_hash')
            url.searchParams.delete('type')
            window.history.replaceState({}, '', url.toString())
          }
          setAuthMode('forgot-password')
          return
        }

        if (result.session) {
          console.log('Session established for password reset')
          setSessionEstablished(true)
        } else {
          setError('Failed to establish session. Please try again.')
          setAuthMode('forgot-password')
        }
      }
    }

    establishSession()
  }, [authMode])

  useEffect(() => {
    return () => {
      checkEmail.cancel()
      checkPassword.cancel()
      checkPasswordMatch.cancel()
    }
  }, [checkEmail, checkPassword, checkPasswordMatch])

  const handleLogin = async (form: HTMLFormElement) => {
    setError(null)
    setIsLoading(true)

    const formData = new FormData(form)
    const email = formData.get('email') as string
    const password = formData.get('password') as string

    if (!validateEmail(email)) {
      setError('Please enter a valid email address')
      setIsLoading(false)
      return
    }

    if (!validatePassword(password)) {
      setError('Password must be at least 8 characters long')
      setIsLoading(false)
      return
    }

    const result = await authenticate(formData, 'login')

    if (result?.error) {
      setError(result.error)
      setIsLoading(false)
    } else {
      // Wait for session to propagate before navigating
      // Increased from 100ms to 300ms to reduce race conditions
      await new Promise((resolve) => setTimeout(resolve, 300))
      // Call onAuthSuccess callback which will close modal and trigger session reload
      onAuthSuccess()
    }
  }

  const handleSignup = async (form: HTMLFormElement) => {
    setError(null)

    // Show password confirmation field if not already shown
    if (!showPasswordConfirm) {
      setShowPasswordConfirm(true)
      return
    }

    setIsLoading(true)

    const formData = new FormData(form)
    const email = formData.get('email') as string
    const password = formData.get('password') as string
    const confirmPassword = formData.get('confirmPassword') as string

    if (!validateEmail(email)) {
      setError('Please enter a valid email address')
      setIsLoading(false)
      return
    }

    if (!validatePassword(password)) {
      setError('Password must be at least 8 characters long')
      setIsLoading(false)
      return
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      setIsLoading(false)
      return
    }

    if (!tosAccepted) {
      setError('You must agree to the TOS')
      setIsLoading(false)
      return
    }

    const result = await authenticate(formData, 'signup')
    setIsLoading(false)

    if (result?.error) {
      setError(result.error)
    } else {
      setAuthMode('confirm')
    }
  }

  const handleForgotPassword = async (form: HTMLFormElement) => {
    setError(null)
    setSuccess(null)
    setIsLoading(true)

    const formData = new FormData(form)
    const email = formData.get('email') as string

    if (!validateEmail(email)) {
      setError('Please enter a valid email address')
      setIsLoading(false)
      return
    }

    const result = await requestPasswordReset(formData)
    setIsLoading(false)

    if (result?.error) {
      setError(result.error)
    } else if (result?.message) {
      setSuccess(result.message)
      form.reset()
    } else {
      setSuccess(
        'If an account exists with this email, a password reset link has been sent.'
      )
      form.reset()
    }
  }

  const handleResetPassword = async (form: HTMLFormElement) => {
    setError(null)
    setSuccess(null)
    setIsLoading(true)

    if (!sessionEstablished) {
      setError(
        'Session not established. Please click the reset link from your email again.'
      )
      setIsLoading(false)
      return
    }

    const formData = new FormData(form)
    const password = formData.get('password') as string
    const confirmPassword = formData.get('confirmPassword') as string

    if (!validatePassword(password)) {
      setError('Password must be at least 8 characters long')
      setIsLoading(false)
      return
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      setIsLoading(false)
      return
    }

    // Use backend endpoint to reset password
    const result = await resetPassword(password)
    setIsLoading(false)

    if (result.error) {
      setError(result.error)
    } else {
      // remove the token_hash and type from search params
      const url = new URL(window.location.href)
      if (url.searchParams.has('token_hash') || url.searchParams.has('type')) {
        url.searchParams.delete('token_hash')
        url.searchParams.delete('type')
        window.history.replaceState({}, '', url.toString())
      }

      setSuccess(result.message || 'Password updated successfully!')
      form.reset()

      // Redirect to login after a short delay
      setTimeout(() => {
        handleBackToLogin()
      }, 2000)
    }
  }

  const handlePasswordChange = (password: string, confirmPassword?: string) => {
    checkPassword(password)
    if (confirmPassword) {
      checkPasswordMatch(password, confirmPassword)
    }
  }

  const handleForgotPasswordClick = () => {
    setAuthMode('forgot-password')
    setError(null)
    setShowPasswordConfirm(false)
  }

  const handleBackToLogin = () => {
    setAuthMode('login')
    setError(null)
    setSuccess(null)
    setShowPasswordConfirm(false)
  }

  const handleBackFromSignup = () => {
    setShowPasswordConfirm(false)
    setError(null)

    // Clear password fields but keep email
    const passwordInput = document.getElementById(
      'password'
    ) as HTMLInputElement
    const confirmPasswordInput = document.getElementById(
      'confirmPassword'
    ) as HTMLInputElement
    if (passwordInput) passwordInput.value = ''
    if (confirmPasswordInput) confirmPasswordInput.value = ''
  }

  const handleOpenTos = () => {
    setShowTOSModal(true)
  }

  return (
    <div style={componentStyles.authWindow.container}>
      {(authMode === 'login' || authMode === 'signup') && (
        <LoginSignupForm
          error={error}
          isLoading={isLoading}
          showPasswordConfirm={showPasswordConfirm}
          onLogin={handleLogin}
          onSignup={handleSignup}
          onForgotPassword={handleForgotPasswordClick}
          onBack={handleBackFromSignup}
          tosAccepted={tosAccepted}
          onTosAcceptedChange={setTosAccepted}
          onOpenTos={handleOpenTos}
        />
      )}
      {authMode === 'forgot-password' && (
        <ForgotPasswordForm
          error={error}
          success={success}
          isLoading={isLoading}
          onSubmit={handleForgotPassword}
          onBack={handleBackToLogin}
        />
      )}
      {authMode === 'reset-password' && (
        <ResetPasswordForm
          error={error}
          success={success}
          isLoading={isLoading}
          sessionEstablished={sessionEstablished}
          onPasswordChange={handlePasswordChange}
          onSubmit={handleResetPassword}
          onBackToLogin={handleBackToLogin}
        />
      )}
      {authMode === 'confirm' && (
        <ConfirmationScreen onBackToLogin={handleBackToLogin} />
      )}
      <div
        className="cf-turnstile"
        data-sitekey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
      ></div>

      {showFooter && (
        <>
          {/* Honcho Icon */}
          <div
            style={{
              marginTop: '8px',
              display: 'flex',
              justifyContent: 'center',
              opacity: 0.81,
            }}
          >
            <svg
              width="40"
              height="36"
              viewBox="0 0 87 77"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ color: colors.primaryText }}
            >
              <path
                d="M55.29 0H38.77V6.89H55.29V0Z"
                fill="currentColor"
                style={{ color: colors.primaryText }}
              />
              <path
                d="M65.95 6.89H55.29V13.69H65.95V6.89Z"
                fill="currentColor"
                style={{ color: colors.primaryText }}
              />
              <path
                d="M72.66 13.69H65.95V20.4H72.66V13.69Z"
                fill="currentColor"
                style={{ color: colors.primaryText }}
              />
              <path
                d="M79.54 20.4H72.66V30.17H79.54V20.4Z"
                fill="currentColor"
                style={{ color: colors.primaryText }}
              />
              <path
                d="M86.33 30.16H79.54V46.54H86.33V30.16Z"
                fill="currentColor"
                style={{ color: colors.primaryText }}
              />
              <path
                d="M79.54 46.55H72.67V56.32H79.54V46.55Z"
                fill="currentColor"
                style={{ color: colors.primaryText }}
              />
              <path
                d="M72.67 56.31H65.95V63.11H72.67V56.31Z"
                fill="currentColor"
                style={{ color: colors.primaryText }}
              />
              <path
                d="M65.95 63.11H55.29V69.96H65.95V63.11Z"
                fill="currentColor"
                style={{ color: colors.primaryText }}
              />
              <path
                d="M55.29 69.96H35.84V76.65H55.29V69.96Z"
                fill="currentColor"
                style={{ color: colors.primaryText }}
              />
              <path
                d="M35.84 63.11H25.18V69.96H35.84V63.11Z"
                fill="currentColor"
                style={{ color: colors.primaryText }}
              />
              <path
                d="M25.18 56.31H18.39V63.11H25.18V56.31Z"
                fill="currentColor"
                style={{ color: colors.primaryText }}
              />
              <path
                d="M18.39 46.55H11.52V56.32H18.39V46.55Z"
                fill="currentColor"
                style={{ color: colors.primaryText }}
              />
              <path
                d="M4.87 33.97V20.4H0V40.8H4.87V46.55H11.52V33.97H4.87Z"
                fill="currentColor"
                style={{ color: colors.primaryText }}
              />
              <path
                d="M18.31 27.17H11.52V33.96H18.31V27.17Z"
                fill="currentColor"
                style={{ color: colors.primaryText }}
              />
              <path
                d="M38.77 6.89H18.39V13.69H38.77V6.89Z"
                fill="currentColor"
                style={{ color: colors.primaryText }}
              />
              <path
                d="M45.5 13.69H38.79V20.4H45.5V13.69Z"
                fill="currentColor"
                style={{ color: colors.primaryText }}
              />
              <path
                d="M18.32 27.17H38.79V20.4H18.39V13.69H4.87V20.4H18.32V27.17Z"
                fill="currentColor"
                style={{ color: colors.primaryText }}
              />
              <path
                d="M38.79 30.16H31.98V36.87H38.79V30.16Z"
                fill="currentColor"
                style={{ color: colors.primaryText }}
              />
              <path
                d="M59.09 30.16H52.28V36.87H59.09V30.16Z"
                fill="currentColor"
                style={{ color: colors.primaryText }}
              />
              <path
                d="M59.16 50.47H31.98V56.31H59.16V50.47Z"
                fill="currentColor"
                style={{ color: colors.primaryText }}
              />
            </svg>
          </div>

          {/* Terms and Privacy Links */}
          <div
            style={{
              marginTop: '8px',
              textAlign: 'center',
              fontSize: '12px',
              color: colors.secondaryText,
            }}
          >
            <a
              href="https://app.honcho.dev/tos"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: colors.tertiaryText,
                textDecoration: 'none',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.textDecoration = 'underline'
                e.currentTarget.style.color = colors.secondaryText
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.textDecoration = 'none'
                e.currentTarget.style.color = colors.tertiaryText
              }}
            >
              Terms of Service
            </a>
            <span style={{ margin: '0 8px' }}>•</span>
            <a
              href="https://app.honcho.dev/privacy"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: colors.tertiaryText,
                textDecoration: 'none',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.textDecoration = 'underline'
                e.currentTarget.style.color = colors.secondaryText
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.textDecoration = 'none'
                e.currentTarget.style.color = colors.tertiaryText
              }}
            >
              Privacy Policy
            </a>
          </div>
        </>
      )}

      {/* Terms of Service Modal */}
      {showTOSModal && <TOSModal onClose={() => setShowTOSModal(false)} />}
    </div>
  )
}
